import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, AlertTriangle, Download, ChevronDown, Layers, Loader2 } from "lucide-react";
import {
  createChainTestList,
  uploadChainTestListVersion,
  setChainTestListAssignments,
  downloadLabTestTemplate,
} from "../../../services/diagnosticLabsService";
import { Drawer, PrimaryButton, SecondaryButton, ProgressBar, IndeterminateBar, plural, isProcessing } from "./ui";
import FileDropzone from "./FileDropzone";
import LabChecklist from "./LabChecklist";

// Upload a new list, or a new version of an existing one. Processing status is
// read from `lists` (kept fresh by the page's polling hook) — the drawer does
// not poll on its own. Optional "apply to labs" is saved only after the upload
// has processed successfully, so a failed sheet never switches any lab.
const Step = ({ index, title, children, muted = false }) => (
  <section className={muted ? "opacity-60" : ""}>
    <div className="flex items-center gap-2 mb-2.5">
      <span className="w-5 h-5 rounded-full bg-[#0F4C81] text-white text-[11px] font-semibold flex items-center justify-center">{index}</span>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
    {children}
  </section>
);

const Stat = ({ label, value, tone = "text-gray-900" }) => (
  <div className="rounded-xl bg-white ring-1 ring-[#E3EEEE] px-3 py-2.5">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
    <p className={`mt-0.5 text-lg font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const TestListUploadDrawer = ({ open, onClose, role, chainId, target, lists, labs, onUploaded, onAssigned, onToast }) => {
  // target: { mode: "new" } | { mode: "version", list }
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showLabs, setShowLabs] = useState(false);
  const [phase, setPhase] = useState("compose"); // compose | uploading | processing | done
  const [progress, setProgress] = useState(0);
  const [listId, setListId] = useState(null);
  const [submitted, setSubmitted] = useState(null); // the upload this drawer created
  const [error, setError] = useState("");
  const [assignState, setAssignState] = useState(null); // null | saving | { ok, message }
  const assignedFor = useRef(null);

  const isNew = target?.mode === "new" && !listId;
  const list = useMemo(
    () => lists.find((l) => l.id === (listId || target?.list?.id)) || target?.list || null,
    [lists, listId, target]
  );
  // Track only the upload this drawer created — right after a version upload the
  // page's list can still carry the previous (already completed) upload.
  const upload = !submitted ? null
    : list?.latest_upload?.id === submitted.id ? list.latest_upload : submitted;
  const settled = phase === "processing" || phase === "done" ? !!upload && !isProcessing(upload) : false;
  const applied = settled && ["completed", "partial"].includes(upload.status); // contract: loan-and-subscription/vendor/models/vendor_chain_test_list.py UPLOAD_APPLIED_STATUSES
  const moving = labs.filter((l) => selected.has(l.id) && l.test_source === "chain"); // contract: chain_test_list_serializers.py serialize_chain_member_labs test_source

  useEffect(() => {
    if (!open) return;
    setName("");
    setFile(null);
    setSelected(new Set());
    setShowLabs(false);
    setPhase("compose");
    setProgress(0);
    setListId(null);
    setSubmitted(null);
    setError("");
    setAssignState(null);
    assignedFor.current = null;
  }, [open, target]);

  // Once the upload is processed, apply the optional lab selection (once).
  useEffect(() => {
    if (!settled || !applied || !list || selected.size === 0 || assignedFor.current === upload.id) return;
    assignedFor.current = upload.id;
    setAssignState("saving");
    setChainTestListAssignments(role, chainId, list.id, [...selected], moving.length > 0)
      .then(() => {
        setAssignState({ ok: true, message: `Applied to ${plural(selected.size, "lab")}.` });
        onAssigned?.();
      })
      .catch((err) => setAssignState({ ok: false, message: err.message || "Couldn't apply the list to the selected labs." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, applied, upload?.id]);

  useEffect(() => {
    if (phase === "processing" && settled) setPhase("done");
  }, [phase, settled]);

  const nameError = isNew && name.trim().length === 0 ? "Give the list a name, e.g. “North India menu”." : "";
  const canSubmit = !!file && !nameError && phase === "compose";

  const submit = async () => {
    setError("");
    setPhase("uploading");
    setProgress(0);
    try {
      const response = isNew
        ? (await createChainTestList(role, chainId, { name: name.trim(), file }, setProgress))?.test_list
        : await uploadChainTestListVersion(role, chainId, list.id, file, setProgress);
      setListId(response.id);
      setSubmitted(response.latest_upload);
      onUploaded?.(response);
      setPhase("processing");
    } catch (err) {
      setPhase("compose");
      setError(err.message || "Upload failed.");
    }
  };

  const tryAnotherFile = () => {
    setFile(null);
    setSubmitted(null);
    setPhase("compose");
    setAssignState(null);
    assignedFor.current = null;
  };

  const busy = phase === "uploading";
  const title = isNew ? "New test list" : `Upload new version · ${list?.name || ""}`;
  const subtitle = isNew
    ? "One Excel = one test list. Apply it to any member labs."
    : `${plural(list?.assigned_lab_ids?.length || 0, "lab")} will switch to the new version once it's processed.`;

  const footer = phase === "done" ? (
    <div className="flex justify-end gap-2">
      {!applied && <SecondaryButton onClick={tryAnotherFile}>Try another file</SecondaryButton>}
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </div>
  ) : (
    <div className="flex items-center justify-between gap-3">
      <button type="button" onClick={() => downloadLabTestTemplate(role).catch((e) => onToast?.("error", e.message))}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F4C81] hover:underline">
        <Download className="w-3.5 h-3.5" /> Template
      </button>
      <div className="flex gap-2">
        <SecondaryButton onClick={onClose} disabled={busy}>{phase === "processing" ? "Close" : "Cancel"}</SecondaryButton>
        {phase !== "processing" && (
          <PrimaryButton onClick={submit} disabled={!canSubmit}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Uploading…" : selected.size ? `Upload & apply to ${plural(selected.size, "lab")}` : "Upload"}
          </PrimaryButton>
        )}
      </div>
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} busy={busy} title={title} subtitle={subtitle} footer={footer}>
      <AnimatePresence mode="wait" initial={false}>
        {phase === "compose" || phase === "uploading" ? (
          <motion.div key="compose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-6 space-y-6">
            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
            {isNew && (
              <Step index={1} title="Name the list">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 255))}
                  disabled={busy}
                  placeholder="e.g. North India menu"
                  className="w-full px-3 py-2 rounded-lg border border-[#CFE0E0] bg-white text-sm focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-[#00A6A6]"
                />
                {name && nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
              </Step>
            )}
            <Step index={isNew ? 2 : 1} title="Choose the Excel">
              <FileDropzone file={file} onChange={setFile} disabled={busy} />
              {busy && (
                <div className="mt-3 space-y-1.5">
                  <ProgressBar value={progress} />
                  <p className="text-[11px] text-gray-500">Uploading… {progress}%</p>
                </div>
              )}
            </Step>
            {isNew && labs.length > 0 && (
              <Step index={3} title="Apply to member labs (optional)">
                <button type="button" onClick={() => setShowLabs((v) => !v)} disabled={busy}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-white ring-1 ring-[#DCEAEA] text-sm hover:ring-[#00A6A6]/40 transition-shadow">
                  <span className="text-gray-700">
                    {selected.size ? `${plural(selected.size, "lab")} selected` : "Choose labs now, or later from the list card"}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showLabs ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {showLabs && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }} className="overflow-hidden">
                      <div className="pt-3">
                        <LabChecklist labs={labs} selected={selected} onChange={setSelected} disabled={busy} maxHeight="max-h-[32vh]" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {moving.length > 0 && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    {plural(moving.length, "lab")} currently on another list will move to this one after processing.
                  </p>
                )}
                <p className="mt-2 text-[11px] text-gray-400">Labs are switched only if the sheet processes successfully.</p>
              </Step>
            )}
          </motion.div>
        ) : (
          <motion.div key="status" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-6 space-y-5">
            {!settled ? (
              <div className="rounded-2xl bg-white ring-1 ring-[#DCEAEA] p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#E6F6F6] flex items-center justify-center">
                    <Layers className="w-5 h-5 text-[#00A6A6]" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Processing {list?.name}</p>
                    <p className="text-xs text-gray-500">Validating rows and building the test list. You can close this — it keeps going.</p>
                  </div>
                </div>
                <IndeterminateBar />
              </div>
            ) : (
              <>
                <div className={`rounded-2xl p-5 ring-1 ${applied ? (upload.status === "partial" ? "bg-amber-50/60 ring-amber-200" : "bg-emerald-50/60 ring-emerald-200") : "bg-red-50/60 ring-red-200"}`}>
                  <div className="flex items-start gap-3">
                    {applied ? (
                      upload.status === "partial"
                        ? <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        : <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    ) : <XCircle className="w-5 h-5 text-red-600 mt-0.5" />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {applied
                          ? upload.status === "partial" ? "Processed with some skipped rows" : "Test list is ready"
                          : upload.status === "superseded" ? "A newer upload replaced this one" : "This sheet couldn't be used"}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-600">
                        {applied
                          ? `${plural(upload.valid_rows, "test")} are live on ${list?.name}.`
                          : upload.failure_message || (upload.stuck ? "Processing stopped responding. Please upload the sheet again." : "The list was left unchanged.")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Rows" value={upload.total_rows} />
                  <Stat label="Valid" value={upload.valid_rows} tone="text-emerald-600" />
                  <Stat label="Skipped" value={upload.invalid_rows} tone={upload.invalid_rows ? "text-amber-600" : "text-gray-400"} />
                </div>
                {upload.error_file && (
                  <a href={upload.error_file} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white ring-1 ring-amber-200 text-sm text-amber-800 hover:bg-amber-50 transition-colors">
                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Download the error report ({plural(upload.invalid_rows, "row")})</span>
                    <Download className="w-4 h-4" />
                  </a>
                )}
                {assignState === "saving" && (
                  <p className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying to {plural(selected.size, "lab")}…</p>
                )}
                {assignState && assignState !== "saving" && (
                  <p className={`flex items-center gap-2 text-xs ${assignState.ok ? "text-emerald-700" : "text-red-600"}`}>
                    {assignState.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {assignState.message}
                  </p>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};

export default TestListUploadDrawer;
